import EditUtils from "../basic/EditUtils";
import DitapPanel from "./DitapPanel";

/**
 * 객체 속성 표시 패널 클래스.</br>
 * A Panel that shows the properties of an object.
 * @alias PanelProperty
 * @constructor
 *
 * @param {object} options 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Element} [options.container] 패널을 생성할 컨테이너 요소.</br> A panel container element.
 * @param {DitapViewer} [options.viewer] 사용할 Ditap Viewer 인스턴스.</br>he DitapViewer instance to use.
 * @param {object} [options.selectedObject] 선택된 객체.</br>The selected object.
 * @param {string[]} [options.properties] 선택 객체의 속성.<br>The properties of selected object.
 * @param {Function} [options.propertyCallback] 속성 콜백 함수.<br>property callback Function.
 *
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const position1 = new Ditap.Cartesian3.fromDegrees(
 *   126.97759025002786,
 *   37.56679755138474
 * );
 * const position2 = new Ditap.Cartesian3.fromDegrees(
 *   126.97764810657144,
 *   37.566518888624664
 * );
 *
 * // LinestringEntity instance 생성
 * const linestringEntity = new Ditap.LinestringEntity(viewer, [
 *   position1,
 *   position2,
 * ]);
 *
 * const linestringEntityProperty = 
 *   {
 *      properties: {
 *        id: "line2",
 *        position1 : position1,
 *        position2 : position2
 *      }
 *   };
 *
 * // 비동기로 속성값을 요청하는 promise를 반환하는 함수
 * function getPropertiesPromise(selectedObject) {
 *   // 첫번째 파라미터로 받은 선택된 객체 인스턴스를 활용하여 콜백함수 작성
 *   if (!selectedObject) {
 *     return;
 *   }
 *   return new Promise((resolve, reject) => {
 *     setTimeout(() => {
 *       resolve([
 *         {
 *           title: "newObject",
 *           properties: {
 *             name: "objectName",
 *             content: "objectContent",
 *           },
 *         },
 *         {
 *           title: "newObject2",
 *           properties: {
 *             name: "objectName2",
 *             content: "objectContent2",
 *           },
 *         },
 *       ]);
 *     }, 2000);
 *   });
 * }
 * const ditapPanelElement = document.createElement("div");
 * ditapPanelElement.classList.add("ditap-panel");
 * document.body.append(ditapPanelElement);
 *
 * const panel = new Ditap.PanelProperty(
 *   {
 *     container: ditapPanelElement,
 *     viewer: viewer,
 *     selectedObject: linestringEntity,
 *     properties: [linestringEntityProperty],
 *     propertyCallback: getPropertiesPromise,
  })
 */
class PanelProperty extends DitapPanel {
  constructor(options) {
    const container = options.container;
    const viewer = options.viewer;
    const selectedObject = options.selectedObject;
    const properties = options.properties;
    const propertyCallback = options.propertyCallback;
    const closeCallback = options.closeCallback;

    super(container, viewer);
    if (container instanceof Element) {
      this._container = container;
    } else {
      return;
    }

    // 헤더 타이틀 추가
    this.setTitle("속성");
    this.setSubtitle("속성의 정보를 확인하세요.");
    this.selectedObject = selectedObject;

    // 속성박스 생성
    const propertyBox = this.createPropertyBox();

    // 헤더 추가
    // this.appendContentWrapper(propertyBox["header"]["header"]);

    // 컨텐츠 추가
    this.appendContentWrapper(propertyBox["content"]["content"]);

    // 노드 세팅
    // this.propertyKey = propertyBox["content"]["key"];
    // this.propertyValue = propertyBox["content"]["value"];
    this.listWrap = propertyBox["content"]["listWrap"];

    // 뷰어 세팅
    this.viewer = viewer;
    this.id = EditUtils.getSelectObjectId(this.viewer, selectedObject);

    // 속성 리스트 추가
    this.properties = properties;
    this.addProperties(properties);

    // 컨테이너 오픈
    this.open();

    // 콜백함수 저장
    this.propertyCallback = propertyCallback;

    // 콜백함수 실행
    this.runPropertyCallback();

    this.changeCloseBtn();
    this.closeCallback = closeCallback;
  }

  /**
   * Run property Callback function.
   * @param {object} properties The properties of Entity
   */
  runPropertyCallback() {
    if (this.propertyCallback instanceof Function) {
      this.propertyCallback(this.selectedObject)
        .then((response) => {
          if (response instanceof Array) {
            this.properties = [...this.properties, ...response];

            // 모든 속성 삭제
            this.removeAllPropertyDiv();

            // 속성 list 생성
            this.addProperties(this.properties);
          } else {
            throw Error("propertyCallback response type error");
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } else if (!(this.propertyCallback === undefined)) {
      console.error("PanelProperty propertyCallback type error");
    }
  }

  /**
   * 패널 객체 속성 목록 추가.</br>
   * Adds a list of object properties to the PanelProperty.
   * @param {object} properties 추가할 객체 속성.</br> The properties of Entity.
   */
  addProperties(properties) {
    for (let i = 0; i < properties.length; i++) {
      const property = properties[i];
      const newDataObject = {
        이름: "값",
        ...property.properties, // 기존 데이터 병합
      };
      if (property.title) {
        this.createPropertyDiv(property.title, newDataObject);
      } else {
        this.createPropertyDiv("unknown", newDataObject);
      }
    }
  }

  updatePositions(positionsString) {
    const propertyItems = document.querySelectorAll(".ditap-property-item");

    propertyItems.forEach((item) => {
      const keyElement = item.querySelector(".ditap-property-item-key");
      if (keyElement) {
        const keyText = keyElement.textContent.trim();
        if (keyText === "POSITIONS") {
          const valueElement = item.querySelector(".ditap-property-item-value");
          if (valueElement) {
            valueElement.textContent = positionsString;
          }
        }
      }
    });
  }

  /*
   * Create a div in the PanelProperty.
   * @param {string} title The Title of Properties.
   * @param {object} properties The properties of Entity.
   */
  createPropertyDiv(title, properties) {
    // 속성 ul 생성
    const propertyBtn = document.createElement("div");
    propertyBtn.classList.add("ditap-property-btn");
    const moreBtn = document.createElement("button");
    moreBtn.classList.add("ditap-property-collapse");
    const titleH2 = document.createElement("h2");
    titleH2.classList.add("ditap-property-title");
    titleH2.textContent = title;
    const list = document.createElement("ul");
    list.classList.add("ditap-property-list");

    propertyBtn.append(moreBtn);
    propertyBtn.append(titleH2);
    this.listWrap.append(propertyBtn);
    this.listWrap.append(list);

    moreBtn.addEventListener("click", () => {
      if (moreBtn.classList.contains("close")) {
        // 열림
        moreBtn.classList.remove("close");
        list.classList.remove("hide");
      } else {
        // 닫힘
        moreBtn.classList.add("close");
        list.classList.add("hide");
      }
    });

    // 속성 li추가

    if (properties) {
      const keys = Object.keys(properties);
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if (properties.hasOwnProperty(key)) {
          const value = properties[key];
          const li = document.createElement("li");
          li.classList.add("ditap-property-item");
          const keyDiv = document.createElement("div");
          keyDiv.classList.add("ditap-property-item-key");
          const valueDiv = document.createElement("div");
          valueDiv.classList.add("ditap-property-item-value");

          keyDiv.textContent = key;
          valueDiv.textContent = value;

          li.append(keyDiv);
          li.append(valueDiv);
          list.append(li);
        }
      }
    }
  }

  /*
   * Create PanelProperty Element.
   * 속성 패널 요소 생성.</br>
   * @returns {object} PanelProperty Element
   */
  createPropertyBox() {
    this.entireWrapper.classList.add("ditap-mobile-bottom", "header-top");
    // * 속성박스 생성
    // # 헤더
    // const header = document.createElement("div");
    // header.classList.add("ditap-panel-header");
    // const key = document.createElement("div");
    // key.classList.add("ditap-panel-key");
    // key.innerText = "이름";
    // const value = document.createElement("div");
    // value.classList.add("ditap-panel-value");
    // value.innerText = "값";

    // header.append(key);
    // header.append(value);

    // # 컨텐츠
    const content = document.createElement("div");
    content.classList.add("ditap-property-content");
    const listWrap = document.createElement("div");
    listWrap.classList.add("ditap-property-list-wrap");
    content.append(listWrap);

    return {
      // header: {
      // header: header,
      // key: key,
      // value: value,
      // },
      content: {
        content: content,
        listWrap: listWrap,
      },
    };
  }

  /*
   * remove a list of object properties.
   */
  removePropertyList() {
    while (this.contentWrapper.firstChild) {
      this.contentWrapper.removeChild(this.contentWrapper.lastChild);
    }
  }

  /*
   * remove all of property Div.
   */
  removeAllPropertyDiv() {
    while (this.listWrap.firstChild) {
      this.listWrap.removeChild(this.listWrap.lastChild);
    }
  }

  /**
   * 패널 닫기.</br>
   * Close panel.
   * @param {Element} element 매개변수가 없는 경우 wrapper 오픈.</br>  If no parameter is entered, close wrapper.
   */
  close(element = undefined) {
    // super.close((element = undefined));
    if (element) {
      element.classList.add("hide");
    } else {
      // close wrapper
      this.entireWrapper.classList.add("hide");
      this.ditapPaneManager?.removePane(this.entireWrapper);

      if (this.ditapPanelManager.openPanel === this) {
        this.ditapPanelManager.setOpenPanel(undefined);
      }
    }

    // close container
    this.container.classList.add("hide");

    if (this.viewer.container.classList.contains("ditap-panel-open")) {
      this.viewer.container.classList.remove("ditap-panel-open");
      this.viewer.resize();
    }

    // 도움말 라벨 위치 변경
    const mobileLabel = this.viewer.container.getElementsByClassName(
      "ditap-mobile-label"
    )[0];
    mobileLabel?.classList.remove("bottom-2");
    mobileLabel?.classList.remove("bottom-1");

    if (this.closeCallback instanceof Function) {
      this.closeCallback();
    }
  }
}

export default PanelProperty;
